import { faArrowLeft, faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Carousel as MantineCarousel } from '@mantine/carousel';
import type { JSX } from 'react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import PageIndicators from './PageIndicators';
import type { IThumbnailCarouselItem } from './ThumbnailCarousel.types';
import ThumbnailItem from './ThumbnailItem';

import * as styles from './ThumbnailCarousel.css';

export const DEFAULT_VISIBLE_COUNT = 8;

interface IThumbnailCarouselProps {
  /** Array of thumbnail items to display in the carousel. */
  items: IThumbnailCarouselItem[];
  /** ID of the currently selected creative. */
  selectedId: string;
  /** Callback when a thumbnail is clicked. */
  onSelect: (id: string) => void;
  /** Number of thumbnails visible at once. Defaults to 8. */
  visibleCount?: number;
}

function ThumbnailCarousel({
  items,
  selectedId,
  onSelect,
  visibleCount = DEFAULT_VISIBLE_COUNT,
}: Readonly<IThumbnailCarouselProps>): JSX.Element | null {
  const { t } = useTranslation();
  const carouselApiRef = useRef<{ scrollTo: (index: number) => void } | null>(null);
  const hasScrolledToInitial = useRef(false);

  const selectedIndex = items.findIndex((c) => c.id === selectedId);

  const handlePrev = (): void => {
    const prevIndex = (selectedIndex - 1 + items.length) % items.length;
    onSelect(items[prevIndex].id);
  };

  const handleNext = (): void => {
    const nextIndex = (selectedIndex + 1) % items.length;
    onSelect(items[nextIndex].id);
  };

  const handlePageSelect = (pageIndex: number): void => {
    const firstItemIndex = pageIndex * visibleCount;
    const targetIndex = Math.min(firstItemIndex, items.length - 1);
    onSelect(items[targetIndex].id);
    carouselApiRef.current?.scrollTo(targetIndex);
  };

  useEffect(() => {
    hasScrolledToInitial.current = false;
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId || !carouselApiRef.current) {
      return;
    }
    const index = items.findIndex((c) => c.id === selectedId);
    if (index >= 0) {
      carouselApiRef.current.scrollTo(index);
    }
  }, [selectedId, items]);

  if (items.length <= 1) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div
        className={styles.carouselRow}
        style={{ '--p-visible-count': visibleCount } as React.CSSProperties}
      >
        <button
          aria-label={t('Previous')}
          className={styles.arrowButton}
          data-name={'carousel-prev'}
          onClick={handlePrev}
          type={'button'}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <MantineCarousel
          align={'start'}
          containScroll={'trimSnaps'}
          draggable={false}
          getEmblaApi={(api): void => {
            carouselApiRef.current = api;
            if (selectedId && !hasScrolledToInitial.current) {
              const index = items.findIndex((c) => c.id === selectedId);
              if (index >= 0) {
                api.scrollTo(index);
                hasScrolledToInitial.current = true;
              }
            }
          }}
          loop={true}
          slideGap={0}
          slideSize={'36px'}
          slidesToScroll={1}
          withControls={false}
        >
          {items.map((creative) => (
            <MantineCarousel.Slide key={creative.id}>
              <ThumbnailItem
                creative={creative}
                isSelected={creative.id === selectedId}
                onSelect={onSelect}
              />
            </MantineCarousel.Slide>
          ))}
        </MantineCarousel>
        <button
          aria-label={t('Next')}
          className={styles.arrowButton}
          data-name={'carousel-next'}
          onClick={handleNext}
          type={'button'}
        >
          <FontAwesomeIcon icon={faArrowRight} />
        </button>
      </div>
      <PageIndicators
        onPageSelect={handlePageSelect}
        selectedIndex={selectedIndex}
        totalItems={items.length}
        visibleCount={visibleCount}
      />
    </div>
  );
}

export default ThumbnailCarousel;
